/**
 * Subscription Scent Slide Component
 * Handles scent selection UI
 */
if (!customElements.get('subscription-scent-slide')) {
  customElements.define(
    'subscription-scent-slide',
    class SubscriptionScentSlide extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = [];
        this.refillLimit = 0;
        this.variantSelections = {};
        this.selectedRefills = 0;
        this.filterButtonHandlers = [];
        this.tooltipHandlers = [];
        this.singleScentPermitted = false;
      }

      connectedCallback() {
        Utils.HeightManager.init(this, '--scent-slide-content-height');
        Utils.setupCommonSlideEventListeners(this, this.handleSlideUpdate.bind(this));

        this.footerButtons = this.closest('subscription-flow')?.querySelector('[data-footer-buttons]');
        this.scentSwatches = this.closest('subscription-flow')?.querySelector('[data-scent-swatches]');

        this.cacheElements();
        this.initializeFromActivePlan();
        this.setupEventListeners();
        this.setupVariantControls();
        this.updateCategoryFilters();
        this.setupTooltips();

        if (this.classList.contains('tw-is-active')) {
          if (this.scentSwatches) {
            this.scentSwatches.classList.add('tw-is-active');
          }
          if (this.footerButtons) {
            this.footerButtons.classList.add('tw-is-scent');
          }
        }
      }

      handleSlideUpdate(data) {
        const currentStep = parseInt(this.dataset.step);
        const isActive = data.step === currentStep;

        if (this.scentSwatches) {
          this.scentSwatches.classList.toggle('tw-is-active', isActive);
        }

        if (this.footerButtons) {
          this.footerButtons.classList.toggle('tw-is-scent', isActive);
        }

        if (isActive) {
          this.resetVariantImages();
        }
      }

      resetVariantImages() {
        if (!this.variantImages?.length) return;

        this.variantImages.forEach((img) => {
          img.classList.remove('tw-is-active');
        });

        const hasActiveVariants = Object.values(this.variantSelections).some((count) => count > 0);

        if (hasActiveVariants) {
          Object.entries(this.variantSelections).forEach(([variantId, count]) => {
            if (count > 0) {
              const variantImages = Array.from(this.variantImages).filter((img) => img.dataset.id === variantId);

              variantImages.forEach((img) => img.classList.add('tw-is-active'));
            }
          });
        } else {
          const defaultImage = Array.from(this.variantImages).find((img) =>
            img.hasAttribute('data-default-variant-img')
          );

          if (defaultImage) {
            defaultImage.classList.add('tw-is-active');
          } else if (this.variantImages.length > 0) {
            this.variantImages[0].classList.add('tw-is-active');
          }
        }
      }

      cacheElements() {
        this.currentVariantCount = this.querySelector('[data-current-variant-count]');
        this.refillLimitPlaceholder = this.querySelector('[data-requested-variant-count]');
        this.refillRemaining = this.querySelector('[data-remaining-variants-count]');
        this.remainingText = this.querySelector('[data-remaining-variants-text]');
        this.successMessage = this.querySelector('[data-success-text]');

        this.form = this.querySelector('[data-form]');
        this.filterBtns = this.querySelectorAll('[data-filter-btn]');

        const variantNodeList = this.form?.querySelectorAll('[data-variant]') || [];
        this.variantList = Array.from(variantNodeList);

        this.variantImages = this.querySelectorAll('[data-variant-img]');
        this.tooltipContainers = this.querySelectorAll('[data-tooltip-container]');

        this.selectedRefills = 0;
      }

      setupTooltips() {
        if (!window.FloatingUIDOM) {
          console.error('Tooltip dependencies missing. Make sure Floating UI is loaded.');
          return;
        }

        if (!this.tooltipContainers?.length) {
          console.log('No tooltip containers found in component');
          return;
        }

        const { computePosition, offset, flip, shift, arrow } = window.FloatingUIDOM;

        this.cleanupTooltipHandlers();
        this.tooltipHandlers = [];

        const updatePosition = (container) => {
          const tooltipButton = container.querySelector('[data-tooltip-button]');
          const tooltip = container.querySelector('[data-tooltip]');
          const arrowElem = tooltip?.querySelector('[data-arrow]') || tooltip?.querySelector('[data-tooltip-arrow]');

          if (!tooltipButton || !tooltip) {
            console.warn('Missing tooltip elements', { container });
            return;
          }

          computePosition(tooltipButton, tooltip, {
            placement: 'top',
            middleware: [offset(6), flip(), shift({ padding: 12 }), arrow({ element: arrowElem })],
          })
            .then(({ x, y, placement, middlewareData }) => {
              Object.assign(tooltip.style, {
                left: `${x}px`,
                top: `${y}px`,
              });

              if (arrowElem && middlewareData.arrow) {
                const { x: arrowX, y: arrowY } = middlewareData.arrow;

                const staticSide = {
                  top: 'bottom',
                  right: 'left',
                  bottom: 'top',
                  left: 'right',
                }[placement.split('-')[0]];

                Object.assign(arrowElem.style, {
                  left: arrowX != null ? `${arrowX}px` : '',
                  top: arrowY != null ? `${arrowY}px` : '',
                  right: '',
                  bottom: '',
                  [staticSide]: '-4px',
                });
              }
            })
            .catch((err) => {
              console.error('Error positioning tooltip:', err);
            });
        };

        const toggleTooltip = (e, action = 'toggle') => {
          e.preventDefault();
          e.stopPropagation();

          let container;

          if (e.target && typeof e.target.closest === 'function') {
            container = e.target.closest('[data-tooltip-container]');
          }

          if (!container && e.currentTarget) {
            container = e.currentTarget.matches('[data-tooltip-container]')
              ? e.currentTarget
              : typeof e.currentTarget.closest === 'function'
              ? e.currentTarget.closest('[data-tooltip-container]')
              : null;
          }

          const tooltip = container.querySelector('[data-tooltip]');

          if (!tooltip) {
            console.warn('No tooltip element found in container', container);
            return;
          }

          const isMobile = window.innerWidth < 1024;
          const isCurrentlyActive = isMobile
            ? tooltip.classList.contains('tw-is-active')
            : tooltip.classList.contains('tw-is-active') && window.getComputedStyle(tooltip).visibility !== 'hidden';

          if (e.type === 'click') {
            this.tooltipContainers.forEach((otherContainer) => {
              if (otherContainer !== container) {
                const otherTooltip = otherContainer.querySelector('[data-tooltip]');
                if (otherTooltip) {
                  otherTooltip.classList.remove('tw-is-active');
                }
              }
            });

            if (isCurrentlyActive) {
              tooltip.classList.remove('tw-is-active');
            } else {
              updatePosition(container);
              tooltip.classList.add('tw-is-active');
            }
          } else {
            if (action === 'add') {
              updatePosition(container);
            }
            tooltip.classList[action]('tw-is-active');
          }
        };

        const show = (e) => toggleTooltip(e, 'add');
        const hide = (e) => toggleTooltip(e, 'remove');

        let events = [];
        if (window.innerWidth < 1024) {
          events = [['click', toggleTooltip]];
        } else {
          events = [
            ['mouseenter', show],
            ['mouseleave', hide],
            ['focus', show],
            ['blur', hide],
          ];
        }

        this.tooltipContainers.forEach((container, index) => {
          const tooltip = container.querySelector('[data-tooltip]');

          if (!tooltip) {
            console.warn(`No [data-tooltip] element found in container ${index + 1}`);
            return;
          }

          tooltip.classList.remove('tw-is-active');

          events.forEach(([eventType, handler]) => {
            container.addEventListener(eventType, handler);
            this.tooltipHandlers.push({ element: container, type: eventType, handler });
          });
        });
      }

      cleanupTooltipHandlers() {
        if (this.tooltipHandlers?.length) {
          this.tooltipHandlers.forEach(({ element, type, handler }) => {
            if (element && type && handler) {
              element.removeEventListener(type, handler);
            }
          });
          this.tooltipHandlers = [];
        }
      }

      updateCategoryFilters() {
        if (!this.filterBtns?.length || !this.variantList.length) return;

        this.filterButtonHandlers = [];

        this.filterBtns.forEach((filterButton) => {
          const clickHandler = (event) => {
            const category = event.currentTarget.dataset.category;
            if (!category) return;

            this.filterBtns.forEach((btn) => {
              btn.classList.toggle('tw-is-active', btn.dataset.category === category);
            });

            this.variantList.forEach((variant) => {
              variant.classList.toggle('tw-is-active', variant.dataset.category === category);
            });
          };

          this.filterButtonHandlers.push({ button: filterButton, handler: clickHandler });
          filterButton.addEventListener('click', clickHandler);
        });
      }

      setupEventListeners() {
        this.unsubscribe.push(
          subscribe('subscription-flow:selected-plan', (data) => {
            if (data.details) {
              this.selectedPlan = data.details;
              this.setRefillLimit(data.details);
            }
          })
        );

        this.unsubscribe.push(
          subscribe('subscription-flow:scent-update', (data) => {
            if (data.variantId) {
              this.updateVariantImageState(data.variantId, data.isActive);
            }
          })
        );

        this.unsubscribe.push(
          subscribe('subscription-flow:change-step', (data) => {
            const thisStep = parseInt(this.dataset.step);
            const isActive = data.step === thisStep;

            if (this.scentSwatches) {
              this.scentSwatches.classList.toggle('tw-is-active', isActive);
            }

            if (this.footerButtons) {
              this.footerButtons.classList.toggle('tw-is-scent', isActive);
            }
          })
        );
      }

      setupVariantControls() {
        if (!this.variantList.length || !this.form) return;

        this.selectedRefills = 0;
        this.variantSelections = {};

        this.variantList.forEach((variant) => {
          const variantId = variant.dataset.id;
          const counter = variant.querySelector('.variants-counter');
          const countInput = counter?.querySelector('.count');
          const minusBtn = counter?.querySelector('.minus');
          const plusBtn = counter?.querySelector('.plus');
          const addButton = variant.querySelector('[data-add-btn]');

          if (!countInput || !minusBtn || !plusBtn) return;

          const initialCount = parseInt(countInput.value) || 0;
          this.variantSelections[variantId] = initialCount;
          this.selectedRefills += initialCount;

          this.updateCounterState(variant, initialCount);
          variant.classList.toggle('tw-is-selected', initialCount > 0);
          variant.classList.toggle('tw-is-current', initialCount > 0);

          if (initialCount > 0) {
            this.updateVariantImageState(variantId);
          }

          if (addButton) {
            addButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              const variantId = variant.dataset.id;
              const countInput = variant.querySelector('.count');

              if (this.singleScentPermitted) {
                if (this.variantSelections[variantId] > 0) {
                  return;
                }

                for (const currentVariantIdInLoop in this.variantSelections) {
                  if (this.variantSelections[currentVariantIdInLoop] > 0 && currentVariantIdInLoop !== variantId) {
                    const previouslySelectedVariantElement = this.variantList.find(
                      (v) => v.dataset.id === currentVariantIdInLoop
                    );
                    const prevCountInput = previouslySelectedVariantElement?.querySelector('.count');

                    this.variantSelections[currentVariantIdInLoop] = 0;
                    if (prevCountInput) prevCountInput.value = 0;

                    if (previouslySelectedVariantElement) {
                      this.updateCounterState(previouslySelectedVariantElement, 0);
                      previouslySelectedVariantElement.classList.remove('tw-is-current', 'tw-is-selected');
                    }
                    publish('subscription-flow:scent-update', {
                      variantId: currentVariantIdInLoop,
                      isActive: false,
                    });
                    break;
                  }
                }

                this.selectedRefills = 0;

                if (countInput) countInput.value = 1;
                this.variantSelections[variantId] = 1;
                this.selectedRefills = 1;

                this.updateCounterState(variant, 1);
                variant.classList.add('tw-is-current', 'tw-is-selected');

                publish('subscription-flow:scent-update', {
                  variantId: variantId,
                  isActive: true,
                });

                this.updateRefillCounters();
                return;
              }

              if (this.selectedRefills >= this.refillLimit) return;

              const currentValue = parseInt(countInput.value) || 0;
              const newValue = currentValue + 1;

              if (countInput) countInput.value = newValue;
              this.variantSelections[variantId] = newValue;
              this.selectedRefills++;

              this.updateCounterState(variant, newValue);
              variant.classList.add('tw-is-current', 'tw-is-selected');
              this.updateRefillCounters();

              if (currentValue === 0 && newValue > 0) {
                publish('subscription-flow:scent-update', {
                  variantId: variantId,
                  isActive: true,
                });
              }
            });
          }

          plusBtn.addEventListener('click', (e) => {
            e.stopPropagation();

            if (this.selectedRefills >= this.refillLimit) return;

            const currentValue = parseInt(countInput.value) || 0;
            const newValue = currentValue + 1;

            countInput.value = newValue;
            this.variantSelections[variantId] = newValue;
            this.selectedRefills++;

            this.updateCounterState(variant, newValue);
            variant.classList.add('tw-is-current', 'tw-is-selected');
            this.updateRefillCounters();

            if (currentValue === 0 && newValue > 0) {
              publish('subscription-flow:scent-update', {
                variantId: variantId,
                isActive: true,
              });
            }
          });

          minusBtn.addEventListener('click', (e) => {
            e.stopPropagation();

            const currentValue = parseInt(countInput.value) || 0;
            if (currentValue <= 0) return;

            const newValue = currentValue - 1;

            countInput.value = newValue;
            this.variantSelections[variantId] = newValue;
            this.selectedRefills--;

            this.updateCounterState(variant, newValue);
            variant.classList.toggle('tw-is-selected', newValue > 0);
            this.updateRefillCounters();

            if (newValue === 0) {
              variant.classList.remove('is-current');
            }
          });

          variant.addEventListener('click', (e) => {
            if (e.target.closest('.variants-counter') || e.target.closest('[data-add-btn]')) return;

            const variantId = variant.dataset.id;
            this.variantImages.forEach((img) => {
              img.classList.remove('tw-is-active');

              if (img.dataset.id === variantId) {
                img.classList.add('tw-is-active');
              }
            });

            this.variantList.forEach((v) => {
              v.classList.toggle('tw-is-viewed', v.dataset.id === variantId);
            });
          });
        });

        this.updateRefillCounters();
        this.updateAddButtonsState();
      }

      updateAddButtonsState() {
        this.variantList.forEach((variant) => {
          const addBtn = variant.querySelector('[data-add-btn]');
          if (addBtn) {
            const variantId = variant.dataset.id;
            const isSelected = this.variantSelections[variantId] > 0;

            if (this.singleScentPermitted) {
              addBtn.classList.toggle('disabled', isSelected);
            } else {
              const isSelectionComplete = this.selectedRefills >= this.refillLimit;
              addBtn.classList.toggle('disabled', isSelectionComplete);
            }
          }
        });
      }

      updateVariantImageState(activeVariantId, isActive = true) {
        if (!this.variantImages?.length) return;

        const activeImagesBeforeChange = Array.from(this.variantImages).filter((img) =>
          img.classList.contains('tw-is-active')
        );

        const isLastActiveBeingRemoved =
          !isActive &&
          activeImagesBeforeChange.length === 1 &&
          activeImagesBeforeChange[0].dataset.id === activeVariantId;

        const isDirectClick = activeVariantId && isActive && !this._isFromCounter;

        if (isDirectClick || (this.refillLimit === 1 && isActive)) {
          this.variantImages.forEach((img) => img.classList.remove('tw-is-active'));
        }

        if (!isLastActiveBeingRemoved) {
          this.variantImages.forEach((image) => {
            const imageVariantId = image.dataset.id;

            if (imageVariantId === activeVariantId) {
              image.classList.toggle('tw-is-active', isActive);
            }
          });
        }

        const anyActive = Array.from(this.variantImages).some((img) => img.classList.contains('tw-is-active'));

        if (!anyActive && this.variantImages.length > 0) {
          const defaultImage = Array.from(this.variantImages).find((img) =>
            img.hasAttribute('data-default-variant-img')
          );

          if (defaultImage) {
            defaultImage.classList.add('tw-is-active');
          } else {
            this.variantImages[0].classList.add('tw-is-active');
          }
        }

        this._isFromCounter = false;
      }

      updateCounterState(variant, count) {
        const minusBtn = variant.querySelector('.minus');
        const plusBtn = variant.querySelector('.plus');

        if (minusBtn) {
          minusBtn.classList.toggle('disabled', count <= 0);
        }

        if (plusBtn) {
          plusBtn.classList.toggle('disabled', this.selectedRefills >= this.refillLimit);
        }

        const isSingleRefill = this.refillLimit === 1;
        variant.classList.toggle('is-custom', isSingleRefill);
        variant.classList.toggle('is-checked', count > 0);
        variant.classList.toggle('tw-is-selected', count > 0);
      }

      updateRefillCounters() {
        if (!this.currentVariantCount || !this.refillRemaining) return;

        this.currentVariantCount.textContent = this.selectedRefills;

        const remainingRefills = this.refillLimit - this.selectedRefills;
        this.refillRemaining.textContent = remainingRefills;

        const hasCompletedSelection = remainingRefills === 0;
        this.successMessage.classList.toggle('tw-is-active', hasCompletedSelection);
        this.remainingText.classList.toggle('tw-is-active', !hasCompletedSelection);

        this.variantList.forEach((variant) => {
          const plusBtn = variant.querySelector('.plus');
          if (plusBtn) {
            plusBtn.classList.toggle('disabled', this.selectedRefills >= this.refillLimit);
          }
        });

        this.updateAddButtonsState();
        this.updateScentSwatches();
        this.publishScentSelection();
      }

      updateScentSwatches() {
        if (!this.scentSwatches) return;

        let variantsImagesEl = this.scentSwatches.querySelector('[data-variant-swatches-images]');

        if (!variantsImagesEl) {
          variantsImagesEl = document.createElement('div');
          variantsImagesEl.setAttribute('data-variant-swatches-images', '');
          variantsImagesEl.className = 'tw-flex tw-gap-2 tw-items-center';
          this.scentSwatches.appendChild(variantsImagesEl);
        }

        const thisStep = parseInt(this.dataset.step);
        const currentStep = parseInt(this.closest('subscription-flow')?.dataset.currentStep || '0');
        const isSlideActive = thisStep === currentStep;
        this.scentSwatches.classList.toggle('tw-is-active', isSlideActive);

        variantsImagesEl.innerHTML = '';

        let selectedVariants = [];

        Object.entries(this.variantSelections).forEach(([variantId, quantity]) => {
          if (quantity > 0) {
            const variantElement = this.variantList.find((v) => v.dataset.id === variantId);

            if (variantElement) {
              for (let i = 0; i < quantity; i++) {
                selectedVariants.push({
                  id: variantId,
                  element: variantElement,
                });
              }
            }
          }
        });

        selectedVariants.slice(0, this.refillLimit).forEach((variant) => {
          const variantImage = variant.element.querySelector('.variants_image-wrapper img');

          if (variantImage) {
            const imgContainer = document.createElement('div');
            imgContainer.className = 'variant-swatch-image tw-is-active tw-relative';
            imgContainer.dataset.variantId = variant.id;

            imgContainer.appendChild(variantImage.cloneNode(true));

            const removeBtn = document.createElement('button');
            removeBtn.className =
              'tw-absolute tw-top-0 -tw-right-[4px] tw-bg-black tw-text-white ' +
              'tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center ' +
              'tw-shadow-sm tw-transform tw-translate-x-1/2 tw-translate-y-[-50%]';
            removeBtn.innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" ' +
              'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
              'stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line>' +
              '<line x1="6" y1="6" x2="18" y2="18"></line></svg>';
            removeBtn.title = 'Remove this refill';
            removeBtn.setAttribute('aria-label', 'Remove this refill');

            removeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              const variantId = variant.id;
              const currentQuantity = this.variantSelections[variantId] || 0;

              if (currentQuantity > 0) {
                this.variantSelections[variantId] = currentQuantity - 1;
                this.selectedRefills--;

                const variantElement = this.variantList.find((v) => v.dataset.id === variantId);
                const countInput = variantElement?.querySelector('.count');
                if (countInput) {
                  countInput.value = currentQuantity - 1;
                  this.updateCounterState(variantElement, currentQuantity - 1);
                }

                if (currentQuantity - 1 === 0) {
                  variantElement?.classList.remove('tw-is-current', 'tw-is-selected');
                  publish('subscription-flow:scent-update', {
                    variantId: variantId,
                    isActive: false,
                  });
                }

                this.updateRefillCounters();
              }
            });

            imgContainer.appendChild(removeBtn);
            variantsImagesEl.appendChild(imgContainer);
          }
        });

        if (selectedVariants.length < this.refillLimit) {
          for (let i = 0; i < this.refillLimit - selectedVariants.length; i++) {
            const placeholder = document.createElement('div');
            placeholder.className = 'placeholder';
            variantsImagesEl.appendChild(placeholder);
          }
        }

        if (selectedVariants.length > this.refillLimit) {
          const additionalCount = document.createElement('span');
          additionalCount.className = 'additional-count';
          additionalCount.textContent = `+${selectedVariants.length - this.refillLimit}`;
          variantsImagesEl.appendChild(additionalCount);
        }
      }

      initializeFromActivePlan() {
        const initialPlanElement = document.querySelector('subscription-plan [data-plan-toggle].tw-is-active');
        if (initialPlanElement) {
          const planElement = initialPlanElement.closest('subscription-plan');
          if (planElement) {
            try {
              const planDetailsElement = planElement.querySelector('[data-plan-details]');
              if (planDetailsElement) {
                const planDetails = Utils.parseJSON(planDetailsElement.textContent);
                this.selectedPlan = planDetails;
                this.setRefillLimit(planDetails);
              }
            } catch (error) {
              console.error('Error initializing from active plan:', error);
            }
          }
        }
      }

      setRefillLimit(planDetails) {
        if (!this.refillLimitPlaceholder) return;

        if (planDetails?.refill_count) {
          const refillLimit = parseInt(planDetails.refill_count);
          this.refillLimit = refillLimit;
          this.refillLimitPlaceholder.textContent = refillLimit;
          this.singleScentPermitted = refillLimit === 1;

          this.cacheElements();
          this.showCorrectHeading();
          this.resetVariantSelections(refillLimit);
          this.resetVariantImages();
        }
      }

      showCorrectHeading() {
        const heading = this.querySelector('[data-heading]');
        const headingMultiple = this.querySelector('[data-heading-multiple]');
        if (!this.singleScentPermitted) {
          heading.classList.add('tw-hidden');
          headingMultiple.classList.remove('tw-hidden');
        } else {
          heading.classList.remove('tw-hidden');
          headingMultiple.classList.add('tw-hidden');
        }
      }

      resetVariantSelections(refillLimit) {
        this.selectedRefills = 0;
        this.variantSelections = {};

        const isSingleRefill = refillLimit === 1;
        const defaultVariants = this.variantList.filter((v) => v.hasAttribute('data-default'));
        let hasSelectedDefault = false;

        this.variantList.forEach((variant) => {
          const countInput = variant.querySelector('.count');
          const isDefault = variant.hasAttribute('data-default');
          const variantId = variant.dataset.id;

          let newCount = 0;

          if (isSingleRefill && isDefault && !hasSelectedDefault) {
            newCount = 1;
            hasSelectedDefault = true;
          }

          if (countInput) {
            countInput.value = newCount;
          }

          this.variantSelections[variantId] = newCount;
          this.selectedRefills += newCount;

          variant.classList.remove('tw-is-current', 'tw-is-selected');
          if (newCount > 0) {
            variant.classList.add('tw-is-current', 'tw-is-selected');
            this.updateVariantImageState(variantId, true);
          }

          this.updateCounterState(variant, newCount);
        });

        if (isSingleRefill && !hasSelectedDefault && this.variantList.length > 0) {
          const firstVariant = this.variantList[0];
          const countInput = firstVariant.querySelector('.count');
          const variantId = firstVariant.dataset.id;

          if (countInput) {
            countInput.value = 1;
          }

          this.variantSelections[variantId] = 1;
          this.selectedRefills = 1;

          firstVariant.classList.add('tw-is-current', 'tw-is-selected');
          this.updateCounterState(firstVariant, 1);
          this.updateVariantImageState(variantId, true);
        }

        this.selectedRefills = Object.values(this.variantSelections).reduce((sum, count) => sum + count, 0);

        this.updateRefillCounters();
      }

      publishScentSelection() {
        publish('subscription-flow:scent-selection', {
          selections: this.variantSelections,
          count: this.selectedRefills,
          limit: this.refillLimit,
          isComplete: this.selectedRefills === this.refillLimit,
        });
      }

      disconnectedCallback() {
        Utils.HeightManager.disconnect(this);

        if (this.filterButtonHandlers) {
          this.filterButtonHandlers.forEach(({ button, handler }) => {
            button.removeEventListener('click', handler);
          });
        }

        this.cleanupTooltipHandlers();

        if (this.variantList) {
          this.variantList.forEach((variant) => {
            const plusBtn = variant.querySelector('.plus');
            const minusBtn = variant.querySelector('.minus');

            if (plusBtn) {
              plusBtn.replaceWith(plusBtn.cloneNode(true));
            }

            if (minusBtn) {
              minusBtn.replaceWith(minusBtn.cloneNode(true));
            }
          });
        }

        if (this.scentSwatches) {
          this.scentSwatches.classList.remove('tw-is-active');
        }

        if (this.footerButtons) {
          this.footerButtons.classList.remove('tw-is-scent');
        }

        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
      }
    }
  );
}
